import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import CopyableTypography from "components/Typography/CopyableTypography";
import { EpisodicEpaFilingFieldsFragment } from "generated-graphql/graphql";

import { formatContainerDateLong } from "../Containers/containerDisplay";
import { EPISODIC_PROJECT_TYPE_LABELS } from "../Submissions/Stepper/rcraNotificationLabels";
import { describeEpaFiling } from "./epaFilingDisplay";

function Field({
  label,
  value,
  copyable = false,
}: {
  label: string;
  value: string;
  copyable?: boolean;
}) {
  return (
    <Stack>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {copyable ? (
        <CopyableTypography text={value} variant="body2" />
      ) : (
        <Typography variant="body2">{value}</Typography>
      )}
    </Stack>
  );
}

/** Read-only view of the EPA filing a notice accepted as evidence, rendered from the notice's snapshot. */
export function EpaFilingRecordDialog({
  filing,
  open,
  onClose,
}: {
  filing: EpisodicEpaFilingFieldsFragment;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title="EPA record of this filing"
      description={describeEpaFiling(filing)}
      cancelButtonProps={{ text: "Close" }}
      primaryActionButton={false}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <Field label="EPA ID" value={filing.handlerId} copyable />
          <Field label="Sequence" value={String(filing.seqNumber)} />
          <Field label="Received by EPA" value={formatContainerDateLong(filing.receiveDate)} />
        </Stack>
        <Field
          label="Reasons"
          value={
            filing.projectTypes.length > 0
              ? filing.projectTypes.map((type) => EPISODIC_PROJECT_TYPE_LABELS[type]).join("; ")
              : "Not reported"
          }
        />
        {filing.otherProjectDescription && (
          <Field label="Other description" value={filing.otherProjectDescription} />
        )}
        <Table size="small">
          <TableHead>
            {/* Headers stay on one line; the description column absorbs the
                wrapping. EPA's record carries no unit — the 8700-12 asks for
                the estimated quantity in pounds. */}
            <TableRow sx={{ "& th": { whiteSpace: "nowrap" } }}>
              <TableCell>Reported waste</TableCell>
              <TableCell>Waste codes</TableCell>
              <TableCell align="right">Est. QTY (lb)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filing.wastes.map((waste, index) => (
              <TableRow key={`${index}-${waste.description ?? ""}`}>
                <TableCell>{waste.description ?? "Not described"}</TableCell>
                <TableCell>{[...waste.wasteCodes, ...waste.stateWasteCodes].join(", ")}</TableCell>
                <TableCell align="right">{waste.estimatedQuantity ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    </Dialog>
  );
}
